// Purpose: Normalize IDs for comparisons (e.g. equality checks, lookups in arrays).
//
// ⚠️ CRITICAL: normalizeDepartmentId MUST NOT be used for Firestore document paths.
// Firestore paths are case-sensitive. Use getFirestorePathDepartmentId() or
// department.id from loaded data instead. Uppercasing causes cross-audits and
// other submissions to be written to one path but read from another.

const safeToText = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') {
    const refLike = v as { id?: unknown; path?: unknown };
    if (typeof refLike.id === 'string') return refLike.id;
    if (typeof refLike.path === 'string') {
      const last = refLike.path.split('/').filter(Boolean).pop();
      if (last) return last;
    }
  }
  try {
    return String(v);
  } catch {
    return '';
  }
};

export const normalizeDepartmentId = (v: unknown): string =>
  safeToText(v).trim().toUpperCase();

/**
 * Compare department IDs when stored values may use hyphens vs underscores (e.g. OPD-SxOrthoEENT vs OPD_SxOrthoEENT).
 * Do not use for Firestore paths — use getFirestorePathDepartmentId.
 */
export const departmentIdScopeKey = (v: unknown): string =>
  normalizeDepartmentId(v).replace(/-/g, '_');

/**
 * Use this when building Firestore paths that include a department ID.
 * Preserves casing (e.g. OPD_SxOrthoEENT); only trims whitespace.
 * @see .cursor/rules/nidata/firestore-department-id-paths.mdc
 */
export const getFirestorePathDepartmentId = (v: unknown): string =>
  safeToText(v).trim();

export const normalizeCategoryId = (v: unknown): string =>
  safeToText(v).trim();
